import {
  ApolloError,
  DocumentNode,
  FetchResult,
  MutationHookOptions,
  MutationTuple,
  OperationVariables,
  TypedDocumentNode,
  useMutation as apolloUseMutation, // eslint-disable-line no-restricted-imports
} from "@apollo/client";
import { datadogRum } from "@datadog/browser-rum";
import { GraphQLFormattedError } from "graphql";
import { useSnackbar } from "notistack";
import { useCallback } from "react";

type AdditionalOptions = {
  onErrorSnackbarMessage?: string;
};

export class GraphQLMutationError extends Error {
  errors: ReadonlyArray<GraphQLFormattedError>;
  constructor(errors: ReadonlyArray<GraphQLFormattedError>) {
    super(errors.map(e => e.message).join("; "));
    this.errors = errors;
  }
}

function extractErrorArray(e: unknown): readonly GraphQLFormattedError[] {
  if (e instanceof GraphQLMutationError) {
    return e.errors;
  }

  if (e instanceof ApolloError) {
    const networkError = e.networkError as any;
    if (networkError?.result?.errors) {
      return networkError.result.errors;
    }

    if (e.graphQLErrors && e.graphQLErrors.length > 0) {
      return e.graphQLErrors;
    }
  }

  return [e as any];
}

export async function runMutationAndNormalizeErrors<Args extends any[], TData>(
  fn: (...args: Args) => Promise<FetchResult<TData>>,
  args: Args
): Promise<FetchResult<TData>> {
  try {
    const result = await fn(...args);
    if (result.errors) {
      throw new GraphQLMutationError(result.errors);
    }

    return result;
  } catch (e) {
    console.error(e);

    const errors = extractErrorArray(e);

    errors.forEach(({ message, locations, path, extensions }) => {
      if (extensions?.code === "INTERNAL_SERVER_ERROR") {
        datadogRum.addError(new Error(message), {
          extra: {
            locations,
            path,
            extensions,
          },
        });
      }
    });

    return { errors };
  }
}

/**
 * A wrapper around Apollo's useMutation hook. This wrapper:
 *
 * - Always returns an "errors" property when there's an error, whether it's a
 *   network-level error (which get thrown by Apollo's useMutation), or a
 *   GraphQL error returned by the backend (which get returned in a `errors`
 *   property by Apollo's useMutation).
 *
 * - Adds a onErrorSnackbarMessage option to automatically show an error
 *   snackbar when there's an error.
 */
export default function useMutation<
  TData = any,
  TVariables = OperationVariables,
>(
  // These types are copied from the type of Apollo's useMutation()
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: MutationHookOptions<TData, TVariables> & AdditionalOptions
): MutationTuple<TData, TVariables> {
  const { enqueueSnackbar } = useSnackbar();

  const [fn, result] = apolloUseMutation(mutation, options);

  const wrappedMutation = useCallback(
    async (...args: Parameters<typeof fn>) => {
      const result = await runMutationAndNormalizeErrors(fn, args);

      if (result.errors && options?.onErrorSnackbarMessage) {
        enqueueSnackbar(options.onErrorSnackbarMessage, { variant: "error" });
      }

      return result;
    },
    [enqueueSnackbar, fn, options?.onErrorSnackbarMessage]
  );

  return [wrappedMutation, result];
}
